import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, Loader2 } from 'lucide-react';
import { IconBrandGoogle, IconBrandOauth } from '@tabler/icons-react';
import '@fontsource-variable/geist/wght.css';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import '../../process-config/ConfigStatusView2.css';
import './CustomersOauthConfig.css';

const API_BASE_URL = getApiBaseUrl();
const OAUTH_API = `${API_BASE_URL}/api/config/vc-google-oauth`;

function Pcs2Section({ icon: Icon, title, desc, children, className = '' }) {
  return (
    <section className={['pcs2-section', className].filter(Boolean).join(' ')}>
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        {desc ? <p className="pcs2-section-desc">{desc}</p> : null}
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function Pcs2TooltipHost({ text, children, align = 'start' }) {
  if (!text) return children;
  return (
    <span
      className={[
        'pcs2-tooltip-host',
        align === 'end' ? 'pcs2-tooltip-host--align-end' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-tooltip={text}
    >
      {children}
    </span>
  );
}

function Pcs2SettingRow({ title, desc, titleIcon: TitleIcon, children }) {
  return (
    <div className="pcs2-setting-row">
      <div className="pcs2-setting-copy">
        <span className="pcs2-setting-title">
          {TitleIcon ? <TitleIcon size={15} strokeWidth={1.75} aria-hidden className="customers-oauth-title-icon" /> : null}
          {title}
        </span>
        {desc ? <span className="pcs2-setting-desc">{desc}</span> : null}
      </div>
      <div className="pcs2-setting-actions">{children}</div>
    </div>
  );
}

function Pcs2Switch({ checked, onChange, ariaLabel, disabled }) {
  const inputRef = useRef(null);
  const handleChange = (e) => {
    onChange(e);
    requestAnimationFrame(() => inputRef.current?.blur());
  };
  return (
    <label className={['pcs2-switch', disabled ? 'pcs2-switch--disabled' : ''].filter(Boolean).join(' ')}>
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
        aria-label={ariaLabel}
      />
      <span className="pcs2-switch-pill">
        <span className="pcs2-switch-dot" />
      </span>
    </label>
  );
}

function snapshotOf(state) {
  return JSON.stringify({
    enabled: Boolean(state.enabled),
    portalBaseUrl: String(state.portalBaseUrl || '').trim(),
  });
}

export default function CustomersOauthConfig() {
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [portalBaseUrl, setPortalBaseUrl] = useState('');
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const isDirty = useMemo(() => {
    if (loading || error || savedSnapshot == null) return false;
    return snapshotOf({ enabled, portalBaseUrl }) !== savedSnapshot;
  }, [loading, error, savedSnapshot, enabled, portalBaseUrl]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(OAUTH_API, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Falha ao carregar autenticação OAuth');
      }
      const nextEnabled = Boolean(data.data?.googleOauthEnabled);
      const nextPortal = String(data.data?.portalBaseUrl || '');
      setEnabled(nextEnabled);
      setPortalBaseUrl(nextPortal);
      setSavedSnapshot(snapshotOf({ enabled: nextEnabled, portalBaseUrl: nextPortal }));
    } catch (e) {
      setError(e.message || 'Erro ao carregar');
      setSavedSnapshot(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCopy = useCallback(
    async (key, value) => {
      const text = String(value || '').trim();
      if (!text) {
        toastError('Nada para copiar');
        return;
      }
      try {
        await navigator.clipboard.writeText(text);
        setCopiedKey(key);
        toastSuccess('Copiado');
        window.setTimeout(() => setCopiedKey((cur) => (cur === key ? null : cur)), 1600);
      } catch {
        toastError('Não foi possível copiar');
      }
    },
    [toastError, toastSuccess]
  );

  const handleSave = useCallback(async () => {
    if (!isDirty || isSaving) return;
    setIsSaving(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(OAUTH_API, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          google_oauth_enabled: enabled,
          portal_base_url: portalBaseUrl,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Falha ao salvar');
      }
      const nextEnabled = Boolean(data.data?.googleOauthEnabled);
      const nextPortal = String(data.data?.portalBaseUrl || '');
      setEnabled(nextEnabled);
      setPortalBaseUrl(nextPortal);
      setSavedSnapshot(snapshotOf({ enabled: nextEnabled, portalBaseUrl: nextPortal }));
      toastSuccess(
        nextEnabled
          ? 'Salvo. Domínio precisa ser liberado no Google Cloud pela Verve.'
          : 'Configuração salva'
      );
    } catch (e) {
      toastError(e.message || 'Erro ao salvar');
    } finally {
      setIsSaving(false);
    }
  }, [isDirty, isSaving, enabled, portalBaseUrl, toastError, toastSuccess]);

  return (
    <div className="pcs2-page customers-oauth-page">
      <Pcs2Section
        className="customers-oauth-section"
        icon={IconBrandOauth}
        title="Autenticação OAuth"
        desc="Ative o login Google do portal Customers e informe a URL"
      >
        {loading ? (
          <ProcessConfigSkeleton variant="settings" embedded ariaLabel="Carregando autenticação OAuth" />
        ) : error ? (
          <div className="customers-oauth-error">
            <span>{error}</span>
            <button type="button" className="customers-oauth-retry" onClick={loadData}>
              Tentar novamente
            </button>
          </div>
        ) : (
          <div className="pcs2-card pcs2-card--flat pcs2-card--spaced">
            <Pcs2SettingRow
              titleIcon={IconBrandGoogle}
              title="Ativar login Google"
              desc="Permite que usuários do portal Customers entrem com conta Google"
            >
              <Pcs2TooltipHost text={enabled ? 'Desativar login Google' : 'Ativar login Google'} align="end">
                <Pcs2Switch
                  checked={enabled}
                  disabled={isSaving}
                  onChange={(e) => setEnabled(e.target.checked)}
                  ariaLabel="Ativar login Google"
                />
              </Pcs2TooltipHost>
            </Pcs2SettingRow>

            <Pcs2SettingRow
              title="URL do portal (frontend)"
              desc="Origem autorizada no Google Cloud (ex.: https://portal.cliente.com)"
            >
              <div className="customers-oauth-field-row">
                <input
                  type="url"
                  className="customers-oauth-input"
                  value={portalBaseUrl}
                  disabled={isSaving}
                  placeholder="https://portal.cliente.com"
                  aria-label="URL do portal Customers"
                  onChange={(e) => setPortalBaseUrl(e.target.value)}
                />
                <Pcs2TooltipHost text="Copiar URL do portal" align="end">
                  <button
                    type="button"
                    className="pcs2-btn-gear"
                    disabled={isSaving || !String(portalBaseUrl || '').trim()}
                    aria-label="Copiar URL do portal"
                    onClick={() => handleCopy('portal', portalBaseUrl)}
                  >
                    {copiedKey === 'portal' ? (
                      <Check size={14} strokeWidth={2} />
                    ) : (
                      <Copy size={14} strokeWidth={1.75} />
                    )}
                  </button>
                </Pcs2TooltipHost>
              </div>
            </Pcs2SettingRow>

          </div>
        )}
      </Pcs2Section>

      {Toaster}

      {typeof document !== 'undefined'
        ? createPortal(
            <button
              type="button"
              className="pcs2-save-fab"
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              aria-label="Salvar configuração"
            >
              {isSaving ? <Loader2 size={14} className="pcs2-spin" aria-hidden /> : null}
              {isSaving ? 'Salvando...' : 'Salvar configuração'}
            </button>,
            document.body
          )
        : null}
    </div>
  );
}
